import type {
  PartialSettingsNotifications,
  UserPreferencesNotificationsContextValues,
  UserPreferencesNotificationsProps,
} from "./user-preferences-notifications.type";

import isEqual from "lodash/isEqual";
import merge from "lodash/merge";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateSettings } from "@/entities/settings";
import { useApiErrorHandler } from "@/modules/api-errors";
import { useAppNotification } from "@/modules/app-notification";

import { getDefaultSettings } from "./user-preferences-notifications.helper";

export const useUserPreferencesNotifications = ({
  notifications,
  savedSettings,
}: UserPreferencesNotificationsProps): UserPreferencesNotificationsContextValues => {
  const { t } = useTranslation();

  const [unsavedSettings, setUnsavedSettings] = useState(savedSettings);
  const defaultSettings = getDefaultSettings(notifications);

  const { mutate, isPending } = useUpdateSettings();
  const { onError } = useApiErrorHandler();
  const { notify } = useAppNotification();

  const isDirty = !isEqual(unsavedSettings, savedSettings);

  const onSettingsChange = (
    newPartialSettings: PartialSettingsNotifications
  ) => {
    const newSettings = merge({}, unsavedSettings, newPartialSettings);
    setUnsavedSettings(newSettings);
  };

  const onCancel = () => {
    setUnsavedSettings(savedSettings);
  };

  const onSave = () => {
    mutate(
      { notifications: unsavedSettings },
      {
        onSuccess: () => {
          notify.success({
            title: t("user:preferences.notifications.updateSuccess"),
          });
        },
        onError,
      }
    );
  };

  return {
    savedSettings,
    unsavedSettings,
    defaultSettings,
    isPending,
    isDirty,
    onSave,
    onCancel,
    onSettingsChange,
  };
};
